import { z } from 'zod';
import { localDate } from './common.js';
/**
 * Therapy homework: a question he was set, for a while, answered each day.
 *
 * ONE PROPERTY MATTERS MORE THAN THE REST, and it is why this is its own module
 * rather than a kind of journal entry:
 *
 *   THE SHARED SURFACE IS NOT A FUNCTION OF THE HOMEWORK.
 *   Not its content, not its cadence, not its existence.
 *
 * `journalCount` is granted at `seem` — the narrowest rung — so a recipient can
 * always see how much he writes, whatever else they are refused. Homework whose
 * subject is a person holding a link would otherwise announce itself in the
 * shape of his writing: a daily cadence beginning the week he saw his therapist
 * and stopping on a date he chose. No content gate touches that. So nothing in
 * the sharing path reads these types, there is no grant for them, and there is
 * no per-entry toggle to get wrong. See migration 034.
 *
 * A QUESTION PLUS FIELDS, not a question plus a text box. "Three positives
 * about EJ" and "rate the anxiety before and after" are the same object with
 * different fields, which is what lets next week's homework be something else
 * entirely without a new feature. The model may COMPOSE these fields from what
 * he says; it may never invent a kind that is not in this file. That is the
 * line between a draft he edits and an app that rewrites itself from a
 * sentence — and it is what keeps "no link can see this" a thing a test can
 * prove rather than a thing anyone hopes.
 */
/** A 0–10 rating. The ends are labelled in his words, or the model's draft of them. */
export const homeworkScale = z.object({
    kind: z.literal('scale'),
    id: z.string().min(1).max(40),
    label: z.string().trim().min(1).max(120),
    low: z.string().trim().max(40),
    high: z.string().trim().max(40),
});
/** One box, as much as he wants to write. */
export const homeworkParagraph = z.object({
    kind: z.literal('paragraph'),
    id: z.string().min(1).max(40),
    label: z.string().trim().min(1).max(120),
});
/**
 * N short lines: "three things that went well".
 *
 * `count` is what the therapist asked for and the app never enforces it. Two
 * filled in is a saved answer, and nothing anywhere says which ones are empty.
 */
export const homeworkLines = z.object({
    kind: z.literal('lines'),
    id: z.string().min(1).max(40),
    label: z.string().trim().min(1).max(120),
    count: z.number().int().min(2).max(5),
});
/** Did you do it. */
export const homeworkYesNo = z.object({
    kind: z.literal('yesno'),
    id: z.string().min(1).max(40),
    label: z.string().trim().min(1).max(120),
});
export const homeworkField = z.discriminatedUnion('kind', [
    homeworkParagraph,
    homeworkLines,
    homeworkScale,
    homeworkYesNo,
]);
/**
 * Four at most. A form longer than this at eleven at night is the rigid end of
 * the axis he named, and no therapist's homework needs five inputs a day.
 */
export const HOMEWORK_MAX_FIELDS = 4;
export const homeworkFields = z.array(homeworkField).min(1).max(HOMEWORK_MAX_FIELDS);
export const homeworkValues = z.record(z.union([z.string().max(4000), z.array(z.string().max(500)).max(5), z.number(), z.boolean(), z.null()]));
/**
 * Is this homework still being asked on `today`?
 *
 * ONE DEFINITION, because the screen announces it and the server enforces it,
 * and a restatement is a second implementation. Inclusive of `endsOn`: a
 * fortnight ending on the 6th includes the 6th, which is what a person means.
 */
export function isOpen(hw, today) {
    if (hw.endedAt !== null)
        return false;
    return hw.startedOn <= today && today <= hw.endsOn;
}
/** Has anything actually been put in this answer? An empty one is not saved. */
export function hasAnything(values) {
    return Object.values(values).some((v) => {
        if (v === null || v === undefined)
            return false;
        if (typeof v === 'string')
            return v.trim().length > 0;
        if (Array.isArray(v))
            return v.some((s) => s.trim().length > 0);
        return true;
    });
}
/**
 * How long a homework may run. Not a preference — a guard against the thing
 * every design in the 22 Sep study called out: homework that never ends stops
 * being a task and becomes a standing reproach.
 */
export const HOMEWORK_MAX_DAYS = 120;
export const createHomework = z
    .object({
    title: z.string().trim().min(1).max(200),
    fields: homeworkFields,
    startedOn: localDate.optional(),
    endsOn: localDate,
})
    .strict();
export const answerHomework = z
    .object({
    values: homeworkValues,
    /** The day it belongs to; the server applies the same 4am grace as a journal entry. */
    day: localDate.optional(),
})
    .strict();
/** What he said out loud, for the model to turn into a draft he then edits. */
export const composeHomework = z.object({ said: z.string().trim().min(1).max(1000) }).strict();
/** Changing one that is already running. Every part is optional. */
export const updateHomework = z
    .object({
    title: z.string().trim().min(1).max(200).optional(),
    fields: homeworkFields.optional(),
    endsOn: localDate.optional(),
})
    .strict();
/**
 * One answer, as lines to show: a label where the field still exists, and the
 * value either way.
 *
 * WHY THIS IS NOT JUST `fields.map(...)`. Changing what a homework asks does
 * not touch what has already been written — answers are keyed by field id, so a
 * box he removes leaves its values behind. Rendering against the CURRENT fields
 * made every one of those disappear from the screen the moment he edited the
 * form, which reads as the app having eaten a fortnight of writing even though
 * the rows are all still there. What he wrote must always be readable.
 *
 * A value whose field is gone keeps its text and loses its label — there is no
 * honest label left to give it, and inventing one would put words on his
 * writing that were never asked.
 *
 * ONE DEFINITION for his own page, the printable sheet and the share payload.
 * They disagreed for about an hour and the share payload was the one that went
 * silently empty.
 */
export function answerLines(fields, values) {
    const seen = new Set();
    const out = [];
    const render = (v) => {
        if (v === null || v === undefined)
            return '';
        if (typeof v === 'boolean')
            return v ? 'Yes' : 'No';
        if (typeof v === 'number')
            return String(v);
        if (Array.isArray(v))
            return v.filter((x) => String(x).trim().length > 0).join('\n');
        return v;
    };
    // The fields it asks for now, in the order it asks them.
    for (const f of fields) {
        seen.add(f.id);
        const text = render(values[f.id] ?? null).trim();
        if (text.length > 0)
            out.push({ label: f.label, text });
    }
    // Then anything written through a box that has since been taken off.
    for (const [id, v] of Object.entries(values)) {
        if (seen.has(id))
            continue;
        const text = render(v).trim();
        if (text.length > 0)
            out.push({ label: null, text });
    }
    return out;
}
//# sourceMappingURL=homework.js.map